import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { DateTime } from 'luxon';
import AppointmentService from '../../services/appointmentService';
import PatientService from '../../services/patientService';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import Button from '../common/Button';
import PatientSectionHeader from '../patients/common/PatientSectionHeader';
import './PatientAppointments.css';

const statusColor = (status) => {
  switch (status) {
    case 'confirmed': return '#28a745';
    case 'in-progress': return '#007bff';
    case 'completed': return '#6c757d';
    case 'cancelled': return '#dc3545';
    case 'no-show': return '#fd7e14';
    default: return '#ffc107'; // scheduled
  }
};

const statusText = (status) => {
  switch (status) {
    case 'scheduled': return 'Programada';
    case 'confirmed': return 'Confirmada';
    case 'in-progress': return 'En Progreso';
    case 'completed': return 'Completada';
    case 'cancelled': return 'Cancelada';
    case 'no-show': return 'No Asistió';
    default: return status;
  }
};

const PatientAppointments = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();

  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!auth.user?.access_token || !patientId) return;
      setLoading(true);
      setError(null);
      try {
        const [patientData, appts] = await Promise.all([
          PatientService.getPatientById(patientId, auth.user.access_token),
          AppointmentService.getAppointmentsByPatient(patientId, auth.user.access_token)
        ]);

        setPatient(patientData);
        const appointmentsArray = Array.isArray(appts) ? appts : (appts?.data || []);
        setAppointments(appointmentsArray);

        // Debug logging
        console.log(`[PatientAppointments] PatientId: ${patientId}`);
        console.log(`[PatientAppointments] Loaded ${appointmentsArray.length} appointments for patient`);
        if (appointmentsArray.length > 0) {
          console.log(`[PatientAppointments] Sample appointment:`, appointmentsArray[0]);
        }
      } catch (e) {
        console.error('[PatientAppointments] Error loading data:', e);
        setError('Error al cargar la agenda del paciente');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patientId, auth.user?.access_token]);

  const enrichedAppointments = useMemo(() => {
    return (appointments || []).map(a => {
      // Convert UTC appointment time to organization timezone
      const utcDt = DateTime.fromISO(a.appointmentDateTime, { zone: 'utc' });
      const orgDt = utcDt.setZone(timezone || 'utc');
      
      return {
        ...a,
        date: orgDt.toLocaleString({ locale: 'es', year: 'numeric', month: 'long', day: 'numeric' }),
        time: orgDt.toFormat('HH:mm'),
        doctorName: a.doctor && a.doctor.name ? a.doctor.name : 'Sin asignar'
      };
    }).sort((x, y) => new Date(y.appointmentDateTime) - new Date(x.appointmentDateTime));
  }, [appointments, timezone]);

  const upcoming = enrichedAppointments.filter(a => new Date(a.appointmentDateTime) >= new Date());
  const past = enrichedAppointments.filter(a => new Date(a.appointmentDateTime) < new Date());

  const openEdit = (appt) => {
    navigate(`/appointments/edit/${appt.id}?patientId=${patientId}`);
  };

  return (
    <div className="patient-appointments-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Agenda de Citas"
        actionButton={
          <Button 
            variant="primary" 
            icon="fas fa-calendar-plus"
            onClick={() => navigate(`/appointments/new?patientId=${patientId}`)}
          >
            Nueva Cita
          </Button>
        }
      />

      {error && <div className="pa-error">{error}</div>}
      {loading && (
        <div className="pa-loading">
          <div className="loading-spinner"></div>
        </div>
      )}

      {!loading && !error && (
        <div className="pa-content">
          <div className="pa-section">
            <h2>Próximas</h2>
            {upcoming.length === 0 ? (
              <div className="pa-empty">No hay citas próximas</div>
            ) : (
              <ul className="pa-list">
                {upcoming.map(a => (
                  <li key={a.id} className="pa-item" onClick={() => openEdit(a)}>
                    <div className="pa-item-main">
                      <div className="pa-item-top">
                        <div className="pa-datetime">
                          <span className="pa-date">{a.date}</span>
                          <span className="pa-time"><i className="fas fa-clock"></i> {a.time}</span>
                        </div>
                        <span className="pa-status" style={{ backgroundColor: statusColor(a.status) }}>{statusText(a.status)}</span>
                      </div>
                      <div className="pa-item-details">
                        <span className="pa-doctor"><i className="fas fa-user-md"></i> {a.doctorName}</span>
                        <span className="pa-procedure"><i className="fas fa-tooth"></i> {a.procedure || 'Sin procedimiento'}</span>
                      </div>
                      {a.notes && <div className="pa-notes">{a.notes}</div>}
                    </div>
                    <i className="fas fa-chevron-right pa-arrow"></i>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pa-section">
            <h2>Pasadas</h2>
            {past.length === 0 ? (
              <div className="pa-empty">No hay citas pasadas</div>
            ) : (
              <ul className="pa-list">
                {past.map(a => (
                  <li key={a.id} className="pa-item" onClick={() => openEdit(a)}>
                    <div className="pa-item-main">
                      <div className="pa-item-top">
                        <div className="pa-datetime">
                          <span className="pa-date">{a.date}</span>
                          <span className="pa-time"><i className="fas fa-clock"></i> {a.time}</span>
                        </div>
                        <span className="pa-status" style={{ backgroundColor: statusColor(a.status) }}>{statusText(a.status)}</span>
                      </div>
                      <div className="pa-item-details">
                        <span className="pa-doctor"><i className="fas fa-user-md"></i> {a.doctorName}</span>
                        <span className="pa-procedure"><i className="fas fa-tooth"></i> {a.procedure || 'Sin procedimiento'}</span>
                      </div>
                      {a.notes && <div className="pa-notes">{a.notes}</div>}
                    </div>
                    <i className="fas fa-chevron-right pa-arrow"></i>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAppointments;
